import { Box } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { RiAddLine } from '@remixicon/react';
import { useNavigate } from '@tanstack/react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo, useRef, useState } from 'react';
import { AgButton } from 'src/components/ui';
import { useAvailableHeight } from 'src/hooks/useAvailableHeight';
import useGridExport from 'src/hooks/useGridExport';
import { DataGrid, GridToolbarV2, useGridRows } from 'src/modules/grids';
import { appLinks } from 'src/routing/appLinks';
import createCustomFieldColumns from 'src/utils/grid/column/createCustomFieldColumns';
import AddBusinessEntityModal from './AddBusinessEntityModal';
import DuplicateBusinessEntitiesModal from './DuplicateBusinessEntitiesModal';
import {
  BUSINESS_ENTITIES_GRID_STORAGE_KEY,
  type BusinessEntityRow,
  useBusinessEntityPage,
} from './hooks/useBusinessEntities';
import { useBusinessEntityColumns } from './hooks/useBusinessEntityColumns';
import { useUpdateBusinessEntity } from './hooks/useBusinessEntityWrites';

/**
 * Business Entities, one server page at a time (TAS-3660). The server sorts by name when no sort is
 * chosen and searches name and bluebook id; Export writes every row under the current search and filters.
 */
export default function BusinessEntitiesGrid() {
  const apiRef = useGridApiRef();
  const flags = useFlags();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const availableHeight = useAvailableHeight(containerRef);

  const enhancedGridState = useGridRows({ mode: 'paged', storageKey: BUSINESS_ENTITIES_GRID_STORAGE_KEY });
  const { rows, loading, pageInfo, totalCount, fetchAllForExport, refetch, reader } =
    useBusinessEntityPage(enhancedGridState);
  const { update: updateBusinessEntity } = useUpdateBusinessEntity();

  const isAccountingEnabled = flags['accounting'];
  const customFieldsConfig = flags['customFields']?.businessEntity;
  const customFields = useMemo(() => createCustomFieldColumns(customFieldsConfig), [customFieldsConfig]);

  const onToggleDisabled = useCallback(
    (entity: BusinessEntityRow) => {
      updateBusinessEntity({ id: entity.id, input: { disabled: !entity.disabled } }).then(() => refetch());
    },
    [updateBusinessEntity, refetch]
  );

  const onEdit = useCallback(
    (entity: BusinessEntityRow) => {
      void navigate(appLinks.settingsEntities({ id: entity.id }));
    },
    [navigate]
  );

  const columns = useBusinessEntityColumns({ customFields, isAccountingEnabled, onToggleDisabled, onEdit });

  const handleOpenModal = useCallback(() => setModalOpen(true), []);
  // Clerk permission checks can replace this seam without changing the cleanup flow.
  const canManageDuplicates = true;
  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    refetch();
  }, [refetch]);

  useGridExport(apiRef, 'business-entities', undefined, undefined, fetchAllForExport);

  const toolbar = useMemo(
    () => (
      <GridToolbarV2
        apiRef={apiRef}
        enhancedGridState={enhancedGridState}
        entityName="Business Entity"
        entityNamePlural="Business Entities"
        toolbarActionsRight={
          <div className="flex items-center gap-2">
            {canManageDuplicates && (
              <AgButton variant="white" onPress={() => setDuplicatesOpen(true)}>
                Find duplicates
              </AgButton>
            )}
            <AgButton variant="primary" onPress={handleOpenModal}>
              New
              <RiAddLine size={16} />
            </AgButton>
          </div>
        }
      />
    ),
    [apiRef, enhancedGridState, handleOpenModal]
  );

  return (
    <>
      {reader}
      <AddBusinessEntityModal handleClose={handleCloseModal} openAddEntity={modalOpen} refetch={refetch} />
      <DuplicateBusinessEntitiesModal
        open={duplicatesOpen}
        onClose={() => setDuplicatesOpen(false)}
        onChanged={() => void refetch()}
      />
      <Box ref={containerRef}>
        <DataGrid
          mode="paged"
          rows={rows}
          totalCount={totalCount}
          pageInfo={pageInfo}
          loading={loading}
          enhancedGridState={enhancedGridState}
          columns={columns}
          gridKey="settings.business.business-entities"
          storageKey={BUSINESS_ENTITIES_GRID_STORAGE_KEY}
          apiRef={apiRef}
          initialState={{ columns: { columnVisibilityModel: { receivablesAccount: false, payablesAccount: false } } }}
          pinnedColumns={{ right: ['actions'] }}
          slots={{ toolbar }}
          variant="list"
          autoHeight={false}
          height={availableHeight}
        />
      </Box>
    </>
  );
}
